import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchBar } from './SearchBar';
import { Textarea } from '../ui/textarea';
import { ModernPageHeader, ModernStatCard } from '../ui/ModernComponents';
import { 
  Plus, 
  FileText, 
  User,
  Car,
  Calendar,
  DollarSign,
  MapPin,
  Clock,
  Play,
  Pause,
  Ban,
  X,
  History,
  CheckCircle,
  Edit,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { apiService } from '../../services/api';

export const LocacoesPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSubAdmin = user?.tipo === 'sub_admin';

  const [locacoes, setLocacoes] = useState([]);
  const [busca, setBusca] = useState('');
  const [clientes, setClientes] = useState([]);
  const [motos, setMotos] = useState([]);
  // Listas completas (sem filtro de status) usadas pelo modal de edição para garantir
  // que o cliente/moto atual da locação sempre apareça mesmo se bloqueado/alugada
  const [clientesTodos, setClientesTodos] = useState([]);
  const [motosTodas, setMotosTodas] = useState([]);

  const qLoc = busca.trim().toLowerCase();
  const locacoesFiltradas = qLoc ? locacoes.filter(l =>
    (l.cliente_nome || '').toLowerCase().includes(qLoc) ||
    (l.moto_placa || '').toLowerCase().includes(qLoc) ||
    String(l.numero_registro || '').includes(qLoc) ||
    (l.status || '').toLowerCase().includes(qLoc)
  ) : locacoes;
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocacao, setEditingLocacao] = useState(null);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [locacaoFinalizar, setLocacaoFinalizar] = useState(null);
  const [kmFinal, setKmFinal] = useState('');
  
  // Estados para controle de status
  const [showAcaoModal, setShowAcaoModal] = useState(false);
  const [acaoSelecionada, setAcaoSelecionada] = useState(null);
  const [locacaoSelecionada, setLocacaoSelecionada] = useState(null);
  const [motivoAcao, setMotivoAcao] = useState('');
  const [observacoesAcao, setObservacoesAcao] = useState('');
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [historicoLocacao, setHistoricoLocacao] = useState([]);
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    moto_id: '',
    plano: '',
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: '',
    km_inicial: '',
    observacoes: ''
  });

  // Função para calcular data de fim baseada no plano
  const calculateEndDate = (plano, startDate) => {
    const start = new Date(startDate);
    let months = 0;
    
    switch (plano) {
      case 'mensal':
        months = 3; // Mínimo 3 meses
        break;
      case 'anual':
        months = 12; // 12 meses
        break;
      case 'fidelidade':
        months = 36; // 36 meses
        break;
      default:
        return '';
    }
    
    const endDate = new Date(start);
    endDate.setMonth(endDate.getMonth() + months);
    return endDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando dados de locações...');
      
      const [locacoesData, clientesData, motosData] = await Promise.all([
        apiService.getLocacoes(),
        apiService.getClientes(),
        apiService.getMotos()
      ]);
      
      console.log('✅ Dados carregados:', {
        locacoes: locacoesData?.length || 0,
        clientes: clientesData?.length || 0,
        motos: motosData?.length || 0
      });
      
      setLocacoes(Array.isArray(locacoesData) ? locacoesData : []);
      setClientesTodos(Array.isArray(clientesData) ? clientesData : []);
      setMotosTodas(Array.isArray(motosData) ? motosData : []);
      setClientes(Array.isArray(clientesData) ? clientesData.filter(c => c.status === 'ativo') : []);
      setMotos(Array.isArray(motosData) ? motosData.filter(m => m.status === 'disponivel') : []);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      alert('Erro ao carregar dados. Por favor, recarregue a página.');
      // Define arrays vazios em caso de erro
      setLocacoes([]);
      setClientes([]);
      setMotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const kmRaw = formData.km_inicial;
      const kmNum = kmRaw === '' || kmRaw === null || kmRaw === undefined
        ? null
        : parseInt(kmRaw, 10);
      if (kmNum === null || Number.isNaN(kmNum) || kmNum < 0) {
        alert('Informe a quilometragem inicial (número válido, 0 ou maior).');
        return;
      }
      const locacaoData = {
        ...formData,
        km_inicial: kmNum
      };

      if (editingLocacao) {
        await apiService.atualizarLocacao(editingLocacao.id, locacaoData);
        alert('Locação atualizada com sucesso!');
      } else {
        await apiService.criarLocacao(locacaoData);
        alert('Locação criada com sucesso!');
      }
      
      setShowModal(false);
      resetForm();
      carregarDados();
    } catch (error) {
      console.error('Erro ao salvar locação:', error);
      const detail = error?.response?.data?.detail;
      let msg = 'Erro ao salvar locação. Verifique os dados e tente novamente.';
      if (typeof detail === 'string') {
        msg = `Erro: ${detail}`;
      } else if (Array.isArray(detail) && detail.length) {
        msg = `Erro de validação: ${detail.map(d => `${(d.loc || []).join('.')} - ${d.msg}`).join(' | ')}`;
      }
      alert(msg);
    }
  };

  const handleFinalizarLocacao = async (e) => {
    e.preventDefault();
    try {
      await apiService.finalizarLocacao(locacaoFinalizar.id, parseInt(kmFinal));
      
      setShowFinalizarModal(false);
      setLocacaoFinalizar(null);
      setKmFinal('');
      carregarDados();
    } catch (error) {
      console.error('Erro ao finalizar locação:', error);
      alert('Erro ao finalizar locação. Tente novamente.');
    }
  };

  const resetForm = () => {
    const hoje = new Date().toISOString().split('T')[0];
    setFormData({
      cliente_id: '',
      moto_id: '',
      plano: '',
      data_inicio: hoje,
      data_fim: '',
      km_inicial: '',
      observacoes: ''
    });
    setEditingLocacao(null);
  };

  const handleEdit = (locacao) => {
    setEditingLocacao(locacao);
    const km = locacao.km_inicial;
    setFormData({
      cliente_id: locacao.cliente_id,
      moto_id: locacao.moto_id,
      plano: locacao.plano,
      data_inicio: locacao.data_inicio ? locacao.data_inicio.split('T')[0] : '',
      data_fim: locacao.data_fim ? locacao.data_fim.split('T')[0] : '',
      km_inicial: km !== null && km !== undefined && km !== '' ? km.toString() : '',
      observacoes: locacao.observacoes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (locacao) => {
    if (window.confirm(`Tem certeza que deseja excluir a locação de ${locacao.cliente_nome}?`)) {
      try {
        await apiService.deletarLocacao(locacao.id);
        carregarDados();
        alert('Locação excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir locação:', error);
        alert('Erro ao excluir locação. Tente novamente.');
      }
    }
  };

  // Atualizar data fim quando plano for selecionado
  const handlePlanoChange = (plano) => {
    const dataFim = calculateEndDate(plano, formData.data_inicio);
    setFormData({
      ...formData,
      plano: plano,
      data_fim: dataFim
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ativa': return 'bg-green-100 text-green-800';
      case 'pausada': return 'bg-yellow-100 text-yellow-800';
      case 'desabilitada': return 'bg-slate-700/50 text-white';
      case 'finalizada': return 'bg-blue-100 text-blue-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      case 'atrasada': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-700/50 text-white';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ativa': return 'Ativa';
      case 'pausada': return 'Pausada';
      case 'desabilitada': return 'Desabilitada';
      case 'finalizada': return 'Finalizada';
      case 'cancelada': return 'Cancelada';
      case 'atrasada': return 'Atrasada';
      default: return status;
    }
  };

  const getPlanoText = (plano) => {
    switch (plano) {
      case 'mensal': return 'Mensal (R$ 57,14/dia - Carência 3 meses)';
      case 'anual': return 'Anual (R$ 50,00/dia - Carência 1 ano)';
      case 'fidelidade': return 'Fidelidade (R$ 60,71/dia - Tudo incluso + Moto Zero Km)';
      default: return plano;
    }
  };

  const getPlanoDetails = (plano) => {
    switch (plano) {
      case 'mensal': return { periodo: '3 meses', descricao: 'Contrato mínimo de 3 meses' };
      case 'anual': return { periodo: '12 meses', descricao: 'Contrato anual com desconto' };
      case 'fidelidade': return { periodo: '36 meses', descricao: 'Moto fica sua ao final' };
      default: return { periodo: '', descricao: '' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch (error) {
      return 'N/A';
    }
  };

  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1; // +1 para incluir o dia inicial
    } catch (error) {
      return 0;
    }
  };

  const getValorTotal = (locacao) => {
    if (locacao.valor_total) return locacao.valor_total;
    if (locacao.valor_diario && locacao.data_inicio && locacao.data_fim) {
      const dias = calculateDays(locacao.data_inicio, locacao.data_fim);
      return locacao.valor_diario * dias;
    }
    return 0;
  };

  const getValorSemanal = (locacao) => {
    if (locacao.valor_semanal && parseFloat(locacao.valor_semanal) > 0) {
      return parseFloat(locacao.valor_semanal);
    }
    if (locacao.valor_diario && parseFloat(locacao.valor_diario) > 0) {
      return parseFloat(locacao.valor_diario) * 7;
    }
    return 0;
  };

  const getClienteNome = (clienteId) => {
    const cliente = [...clientes, ...locacoes.map(l => ({id: l.cliente_id, nome: l.cliente_nome}))].find(c => c.id === clienteId);
    return cliente?.nome || 'Cliente não encontrado';
  };

  const getMotoPlaca = (motoId) => {
    const moto = [...motos, ...locacoes.map(l => ({id: l.moto_id, placa: l.moto_placa}))].find(m => m.id === motoId);
    return moto?.placa || 'Moto não encontrada';
  };

  // Funções para controle de status de locação
  const abrirModalAcao = (acao, locacao) => {
    setAcaoSelecionada(acao);
    setLocacaoSelecionada(locacao);
    setMotivoAcao('');
    setObservacoesAcao('');
    setShowAcaoModal(true);
  };

  const executarAcao = async (e) => {
    e.preventDefault();
    try {
      const locacaoId = locacaoSelecionada.id;
      
      switch (acaoSelecionada) {
        case 'pausar':
          await apiService.pausarLocacao(locacaoId, motivoAcao, observacoesAcao);
          break;
        case 'despausar':
          await apiService.despausarLocacao(locacaoId, motivoAcao, observacoesAcao);
          break;
        case 'desabilitar':
          await apiService.desabilitarLocacao(locacaoId, motivoAcao, observacoesAcao);
          break;
        case 'reabilitar':
          await apiService.reabilitarLocacao(locacaoId, motivoAcao, observacoesAcao);
          break;
        case 'cancelar':
          await apiService.cancelarLocacao(locacaoId, motivoAcao, observacoesAcao);
          break;
      }
      
      setShowAcaoModal(false);
      carregarDados();
    } catch (error) {
      console.error('Erro ao executar ação:', error);
      alert(`Erro ao ${acaoSelecionada} locação. Tente novamente.`);
    }
  };

  const visualizarHistorico = async (locacao) => {
    try {
      const historico = await apiService.getHistoricoLocacao(locacao.id);
      setHistoricoLocacao(historico);
      setLocacaoSelecionada(locacao);
      setShowHistoricoModal(true);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      alert('Erro ao carregar histórico da locação.');
    }
  };

  const formatarDataHora = (dataString) => {
    return new Date(dataString).toLocaleString('pt-BR');
  };

  // Listas para os selects do modal:
  // - se editando, garante que o cliente/moto atual da locação aparece mesmo bloqueado/alugada
  const clientesParaSelect = (() => {
    if (!editingLocacao) return clientes;
    const ja = clientes.some(c => c.id === editingLocacao.cliente_id);
    if (ja) return clientes;
    const atual = clientesTodos.find(c => c.id === editingLocacao.cliente_id);
    return atual ? [atual, ...clientes] : clientes;
  })();
  const motosParaSelect = (() => {
    if (!editingLocacao) return motos;
    const ja = motos.some(m => m.id === editingLocacao.moto_id);
    if (ja) return motos;
    const atual = motosTodas.find(m => m.id === editingLocacao.moto_id);
    return atual ? [atual, ...motos] : motos;
  })();

  const getAcaoText = (acao) => {
    switch (acao) {
      case 'pausar': return 'Pausada';
      case 'despausar': return 'Despausada';
      case 'desabilitar': return 'Desabilitada';
      case 'reabilitar': return 'Reabilitada';
      case 'cancelar': return 'Cancelada';
      default: return acao;
    }
  };

  const getStatusActionButtons = (locacao) => {
    const buttons = [];
    
    if (locacao.status === 'ativa') {
      buttons.push(
        <Button
          key="pausar"
          variant="outline"
          size="sm"
          className="text-yellow-600 hover:text-yellow-700 border-yellow-300"
          onClick={() => abrirModalAcao('pausar', locacao)}
        >
          <Pause className="h-3 w-3 mr-1" />
          Pausar
        </Button>
      );
      buttons.push(
        <Button
          key="desabilitar"
          variant="outline"
          size="sm"
          className="text-gray-300 hover:text-gray-200 border-white/20"
          onClick={() => abrirModalAcao('desabilitar', locacao)}
        >
          <Ban className="h-3 w-3 mr-1" />
          Desabilitar
        </Button>
      );
      buttons.push(
        <Button
          key="cancelar"
          variant="outline"
          size="sm"
          className="text-rose-400 hover:text-rose-300 border-red-300"
          onClick={() => abrirModalAcao('cancelar', locacao)}
        >
          <X className="h-3 w-3 mr-1" />
          Cancelar
        </Button>
      );
    } else if (locacao.status === 'pausada') {
      buttons.push(
        <Button
          key="despausar"
          variant="outline"
          size="sm"
          className="text-emerald-400 hover:text-emerald-300 border-green-300"
          onClick={() => abrirModalAcao('despausar', locacao)}
        >
          <Play className="h-3 w-3 mr-1" />
          Despausar
        </Button>
      );
    } else if (locacao.status === 'desabilitada') {
      buttons.push(
        <Button
          key="reabilitar"
          variant="outline"
          size="sm"
          className="text-blue-400 hover:text-blue-300 border-blue-300"
          onClick={() => abrirModalAcao('reabilitar', locacao)}
        >
          <CheckCircle className="h-3 w-3 mr-1" />
          Reabilitar
        </Button>
      );
    }
    
    return buttons;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400">Carregando locações...</p>
        </div>
      </div>
    );
  }

  // Calcular estatísticas
  const locacoesAtivas = locacoes.filter(l => l.status === 'ativa').length;
  const locacoesPausadas = locacoes.filter(l => l.status === 'pausada').length;
  const locacoesFinalizadas = locacoes.filter(l => l.status === 'finalizada').length;

  return (
    <div className="space-y-6 min-h-screen pb-20" data-testid="locacoes-page">
      {/* Header Moderno */}
      <ModernPageHeader
        title="Gestão de Locações"
        subtitle={`${locacoes.length} locações registradas`}
        icon={FileText}
        gradient="from-purple-900 to-slate-900"
        actions={
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl px-6" onClick={() => { if (!isSubAdmin) resetForm(); }} disabled={isSubAdmin}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Locação
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-slate-800 border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingLocacao ? 'Editar Locação' : 'Nova Locação'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 pb-6">
              <div>
                <Label htmlFor="cliente_id" className="text-gray-300">Cliente</Label>
                <Select value={formData.cliente_id} onValueChange={(value) => setFormData({...formData, cliente_id: value})}>
                  <SelectTrigger className="bg-slate-700 border-white/10 text-white">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientesParaSelect.map(cliente => (
                      <SelectItem key={cliente.id} value={cliente.id}>
                        {cliente.nome} - {cliente.cpf}{cliente.status && cliente.status !== 'ativo' ? ` (${cliente.status})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="moto_id" className="text-gray-300">Moto Disponível</Label>
                <Select value={formData.moto_id} onValueChange={(value) => {
                  const moto = motosTodas.find(m => m.id === value) || motos.find(m => m.id === value);
                  setFormData({
                    ...formData, 
                    moto_id: value,
                    km_inicial: moto && moto.km_atual != null ? moto.km_atual.toString() : ''
                  });
                }}>
                  <SelectTrigger className="bg-slate-700 border-white/10 text-white">
                    <SelectValue placeholder="Selecione a moto" />
                  </SelectTrigger>
                  <SelectContent>
                    {motosParaSelect.map(moto => (
                      <SelectItem key={moto.id} value={moto.id}>
                        {moto.placa} - DK 160 {moto.cor} ({moto.km_atual ? moto.km_atual.toLocaleString() : 'N/A'} km){moto.status && moto.status !== 'disponivel' ? ` — ${moto.status}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="plano" className="text-gray-300">Plano de Locação</Label>
                <Select value={formData.plano} onValueChange={handlePlanoChange}>
                  <SelectTrigger className="bg-slate-700 border-white/10 text-white">
                    <SelectValue placeholder="Selecione o plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">
                      <div className="flex flex-col">
                        <span className="font-medium">Plano Mensal - R$ 57,14/dia</span>
                        <span className="text-xs text-gray-400">Contrato mínimo: 3 meses - Troca de óleo inclusa</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="anual">
                      <div className="flex flex-col">
                        <span className="font-medium">Plano Anual - R$ 50,00/dia</span>
                        <span className="text-xs text-gray-400">Contrato: 12 meses - Troca de óleo inclusa</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="fidelidade">
                      <div className="flex flex-col">
                        <span className="font-medium">Plano Fidelidade - R$ 60,71/dia</span>
                        <span className="text-xs text-gray-400">Contrato: 36 meses - Tudo incluso + Moto Zero Km</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {formData.plano && (
                  <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="text-sm">
                      <p className="font-medium text-blue-200">
                        {getPlanoDetails(formData.plano).descricao}
                      </p>
                      <p className="text-blue-300/80 text-xs mt-0.5">
                        Período: {getPlanoDetails(formData.plano).periodo}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_inicio" className="text-gray-300">Data de Início</Label>
                  <Input
                    id="data_inicio"
                    type="date"
                    value={formData.data_inicio}
                    onChange={(e) => {
                      const novaDataInicio = e.target.value;
                      const novaDataFim = formData.plano ? calculateEndDate(formData.plano, novaDataInicio) : '';
                      setFormData({
                        ...formData, 
                        data_inicio: novaDataInicio,
                        data_fim: novaDataFim
                      });
                    }}
                    required
                    className="bg-slate-700 border-white/10 text-white [color-scheme:dark]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Você pode alterar a data de início
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="data_fim" className="text-gray-300">Data de Término</Label>
                  <Input
                    id="data_fim"
                    type="date"
                    value={formData.data_fim}
                    onChange={(e) => setFormData({...formData, data_fim: e.target.value})}
                    required
                    className="bg-slate-700 border-white/10 text-white [color-scheme:dark]"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Ajustada automaticamente ou edite manualmente
                  </p>
                </div>
              </div>
              
              {formData.plano && formData.data_fim && (
                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                  <div className="text-sm">
                    <p className="font-medium text-emerald-200">
                      📅 Resumo do Contrato
                    </p>
                    <div className="mt-2 space-y-1 text-emerald-300/90">
                      <p>• Início: {formatDate(formData.data_inicio)}</p>
                      <p>• Término: {formatDate(formData.data_fim)}</p>
                      <p>• Duração: {calculateDays(formData.data_inicio, formData.data_fim)} dias</p>
                      <p>• Plano: {getPlanoText(formData.plano)}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="km_inicial" className="text-gray-300">Quilometragem Inicial</Label>
                <Input
                  id="km_inicial"
                  type="number"
                  value={formData.km_inicial}
                  onChange={(e) => setFormData({...formData, km_inicial: e.target.value})}
                  placeholder="KM atual da moto"
                  required
                  readOnly={!editingLocacao && formData.km_inicial !== ''}
                  className="bg-slate-700 border-white/10 text-white"
                />
                {editingLocacao && (
                  <p className="text-xs text-gray-400 mt-1">
                    Você pode editar manualmente se a moto não tiver KM cadastrada.
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="observacoes" className="text-gray-300">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações sobre a locação..."
                  className="bg-slate-700 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl">
                  {editingLocacao ? 'Salvar alterações' : 'Criar Locação'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-xl"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
      />

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ModernStatCard
          icon={FileText}
          value={locacoes.length}
          label="Total de Locações"
          gradient="from-purple-600 to-purple-800"
        />
        <ModernStatCard
          icon={CheckCircle}
          value={locacoesAtivas}
          label="Ativas"
          gradient="from-green-600 to-emerald-800"
        />
        <ModernStatCard
          icon={Pause}
          value={locacoesPausadas}
          label="Pausadas"
          gradient="from-yellow-600 to-orange-700"
        />
        <ModernStatCard
          icon={Ban}
          value={locacoesFinalizadas}
          label="Finalizadas"
          gradient="from-gray-600 to-gray-800"
        />
      </div>

      {/* Modal para finalizar locação */}
      <Dialog open={showFinalizarModal} onOpenChange={setShowFinalizarModal}>
        <DialogContent className="bg-slate-800 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Finalizar Locação</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleFinalizarLocacao} className="space-y-4">
            <div>
              <p className="text-sm text-gray-300 mb-4">
                Finalizando locação de: {locacaoFinalizar?.cliente_nome || getClienteNome(locacaoFinalizar?.cliente_id)}
              </p>
              
              <Label htmlFor="km_final">Quilometragem Final</Label>
              <Input
                id="km_final"
                type="number"
                value={kmFinal}
                onChange={(e) => setKmFinal(e.target.value)}
                placeholder="KM atual da moto"
                min={locacaoFinalizar?.km_inicial || 0}
                required
                className="bg-slate-700 border-white/10"
              />
              
              {kmFinal && locacaoFinalizar && (
                <p className="text-sm text-gray-300 mt-2">
                  KM percorrido: {parseInt(kmFinal) - locacaoFinalizar.km_inicial} km
                </p>
              )}
            </div>
            
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 rounded-xl">
                Finalizar Locação
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowFinalizarModal(false)}
                className="flex-1 bg-white/5 border-white/10 hover:bg-white/10 rounded-xl"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal para ações de status (pausar, desabilitar, cancelar, etc.) */}
      <Dialog open={showAcaoModal} onOpenChange={setShowAcaoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {acaoSelecionada === 'pausar' && 'Pausar Locação'}
              {acaoSelecionada === 'despausar' && 'Despausar Locação'}
              {acaoSelecionada === 'desabilitar' && 'Desabilitar Locação'}
              {acaoSelecionada === 'reabilitar' && 'Reabilitar Locação'}
              {acaoSelecionada === 'cancelar' && 'Cancelar Locação'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={executarAcao} className="space-y-4">
            <div>
              <p className="text-sm text-gray-300 mb-4">
                {acaoSelecionada === 'pausar' && 'Pausando locação de: '}
                {acaoSelecionada === 'despausar' && 'Despausando locação de: '}
                {acaoSelecionada === 'desabilitar' && 'Desabilitando locação de: '}
                {acaoSelecionada === 'reabilitar' && 'Reabilitando locação de: '}
                {acaoSelecionada === 'cancelar' && 'Cancelando locação de: '}
                <strong>{locacaoSelecionada?.cliente_nome || getClienteNome(locacaoSelecionada?.cliente_id)}</strong>
              </p>
              
              <div className="mb-4">
                <Label htmlFor="motivo">Motivo *</Label>
                <Input
                  id="motivo"
                  type="text"
                  value={motivoAcao}
                  onChange={(e) => setMotivoAcao(e.target.value)}
                  placeholder="Informe o motivo da ação..."
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações (opcional)</Label>
                <Textarea
                  id="observacoes"
                  value={observacoesAcao}
                  onChange={(e) => setObservacoesAcao(e.target.value)}
                  placeholder="Observações adicionais..."
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                className={`flex-1 ${
                  acaoSelecionada === 'pausar' ? 'bg-yellow-600 hover:bg-yellow-700' :
                  acaoSelecionada === 'despausar' ? 'bg-green-600 hover:bg-green-700' :
                  acaoSelecionada === 'desabilitar' ? 'bg-gray-600 hover:bg-gray-700' :
                  acaoSelecionada === 'reabilitar' ? 'bg-blue-600 hover:bg-blue-700' :
                  acaoSelecionada === 'cancelar' ? 'bg-red-600 hover:bg-red-700' :
                  'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                Confirmar {getAcaoText(acaoSelecionada)}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAcaoModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal para histórico de locação */}
      <Dialog open={showHistoricoModal} onOpenChange={setShowHistoricoModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {getClienteNome(locacaoSelecionada?.cliente_id)} - Locação #{locacaoSelecionada?.numero_registro || locacaoSelecionada?.id.slice(-8)} - Histórico
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {historicoLocacao.length === 0 ? (
              <p className="text-gray-400 text-center py-4">
                Nenhuma alteração registrada para esta locação.
              </p>
            ) : (
              historicoLocacao.map((item) => (
                <div key={item.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-white">
                        {getAcaoText(item.acao)}
                      </p>
                      <p className="text-sm text-gray-300">
                        {item.status_anterior} → {item.status_novo}
                      </p>
                    </div>
                    <p className="text-xs text-gray-400">
                      {formatarDataHora(item.data_acao)}
                    </p>
                  </div>
                  
                  <div className="text-sm">
                    <p className="text-gray-200">
                      <strong>Motivo:</strong> {item.motivo}
                    </p>
                    {item.observacoes && (
                      <p className="text-gray-200 mt-1">
                        <strong>Observações:</strong> {item.observacoes}
                      </p>
                    )}
                    <p className="text-gray-400 text-xs mt-1">
                      Responsável: {item.usuario_responsavel}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowHistoricoModal(false)}
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🔎 Busca */}
      <SearchBar
        value={busca}
        onChange={setBusca}
        placeholder="Buscar por cliente, placa, nº registro ou status..."
        testId="locacoes-busca-input"
        resultado={busca ? locacoesFiltradas.length : undefined}
      />

      {/* Mensagem quando não há locações */}
      {locacoes.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/5">
          <FileText className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhuma locação cadastrada
          </h3>
          <p className="text-gray-400 mb-6">
            Comece criando sua primeira locação clicando no botão acima
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {locacoesFiltradas.map((locacao) => {
            // Validar se a locação tem os campos mínimos necessários
            if (!locacao || !locacao.id) {
              console.warn('Locação inválida encontrada:', locacao);
              return null;
            }
            
            return (
          <div key={locacao.id} className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-700/50 to-slate-800/50 border border-white/5 backdrop-blur-sm transition-all duration-300 hover:border-white/10 hover:shadow-xl">
            <div className="p-5">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-white text-lg">
                    {getClienteNome(locacao.cliente_id)} - Locação #{locacao.numero_registro || locacao.id.slice(-8)}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {getPlanoDetails(locacao.plano || 'mensal').descricao}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  locacao.status === 'ativa' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                  locacao.status === 'pausada' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                  locacao.status === 'finalizada' ? 'bg-gray-500/20 text-gray-400 border border-gray-500/30' :
                  locacao.status === 'cancelada' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                  'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                }`}>
                  {getStatusText(locacao.status || 'ativa')}
                </span>
              </div>
              
              {/* Detalhes */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <User className="h-4 w-4 text-gray-500" />
                  <span>{getClienteNome(locacao.cliente_id)}</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-300">
                  <Car className="h-4 w-4 text-gray-500" />
                  <span>{getMotoPlaca(locacao.moto_id)} - DK 160</span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-300">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>
                    {formatDate(locacao.data_inicio)} até {formatDate(locacao.data_fim)}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-300">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span>
                    {calculateDays(locacao.data_inicio, locacao.data_fim)} dias
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>
                    KM: {locacao.km_inicial ? locacao.km_inicial.toLocaleString() : 'N/A'}
                    {locacao.km_final && ` → ${locacao.km_final.toLocaleString()}`}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 text-gray-300">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                  <span>
                    Total: R$ {getValorTotal(locacao).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                </div>
                
                {/* Valor Semanal - somente exibição */}
                {locacao.status === 'ativa' && (
                  <div className="flex items-center gap-2 mt-1 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <DollarSign className="h-4 w-4 text-emerald-400" />
                    <span className="text-emerald-300 font-medium">
                      Semanal: R$ {getValorSemanal(locacao).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    {locacao.valor_semanal_manual && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-semibold">MANUAL</span>
                    )}
                  </div>
                )}
              </div>
              
              {locacao.observacoes && (
                <div className="mt-3 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-xs text-blue-300">{locacao.observacoes}</p>
                </div>
              )}
              
              {/* Botões de ação */}
              <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                {locacao.status === 'ativa' && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white rounded-xl"
                    onClick={() => {
                      setLocacaoFinalizar(locacao);
                      setKmFinal('');
                      setShowFinalizarModal(true);
                    }}
                  >
                    Finalizar Locação
                  </Button>
                )}
                
                {/* Botões de controle de status */}
                {getStatusActionButtons(locacao).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {getStatusActionButtons(locacao)}
                  </div>
                )}
                
                {/* Botão de histórico */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white rounded-xl"
                  onClick={() => visualizarHistorico(locacao)}
                >
                  <History className="h-3 w-3 mr-1" />
                  Ver Histórico
                </Button>
                
                {/* Botões de Editar e Excluir — ocultos para sub_admin */}
                {!isSubAdmin && <>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white rounded-xl"
                    onClick={() => handleEdit(locacao)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-xl"
                    onClick={() => handleDelete(locacao)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir
                  </Button>
                </div>
                </>}
              </div>
            </div>
          </div>
            );
          })}
        </div>
      )}
    </div>
  );
};